import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const pillars = [
  {
    icon: '🤝',
    title: 'Honesty Above All',
    desc: 'We insist on truthful profiles. From the very first interaction, every member is expected to represent themselves accurately — because genuine marriages begin with genuine introductions.',
  },
  {
    icon: '🛡️',
    title: 'Safety Without Compromise',
    desc: 'Mobile verification, controlled visibility, and a team that actively reviews the platform ensure that every person here feels protected and respected.',
  },
  {
    icon: '🌸',
    title: 'Dignity in Every Step',
    desc: 'The search for a life partner should feel hopeful, not exhausting. We design every interaction to feel warm, unhurried, and deeply respectful.',
  },
  {
    icon: '👨‍👩‍👧‍👦',
    title: 'Family at the Centre',
    desc: 'In Indian marriages, families matter. Our platform is built to welcome family involvement at whatever level the member is comfortable with.',
  },
];

const whyPoints = [
  { icon: '📱', heading: 'Phone-Verified Members', body: 'No profile goes live without mobile verification. This one step eliminates the vast majority of fake accounts before they ever reach you.' },
  { icon: '🔒', heading: 'Information You Control', body: 'Your address, your phone number, your personal details — they remain yours. You choose exactly when and to whom you reveal sensitive information.' },
  { icon: '🕊️', heading: 'No Pressure Environment', body: 'Weddyfyp does not nudge you to buy upgrades every few seconds or flood your inbox with generic matches. We respect your pace and your process.' },
  { icon: '🇮🇳', heading: 'Built for India', body: 'From the communities we serve to the languages we understand, Weddyfyp is shaped by and for the diversity of Indian families — wherever they live in the world.' },
];

export default function About() {
  return (
    <div style={{ paddingTop: '70px' }}>
  <Helmet>
        <title>About Us - Weddyfyp Matrimony</title>
        <meta name="description" content="Learn about Weddyfyp — our mission to help Indians and NRIs find genuine life partners through a trusted, privacy-first matrimony platform." />
        <link rel="canonical" href="https://weddyfyp.com/about" />
      </Helmet>

      {/* ── Hero ── */}
      <section style={{
        background: 'linear-gradient(150deg, #fdf5f8 0%, #fdf5f8 100%)',
        padding: '80px 24px 70px',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <div style={{ fontSize: '52px', marginBottom: '18px' }}>🪷</div>
          <p style={{ fontSize: '12px', color: '#C2185B', fontWeight: '700', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '14px' }}>WHO WE ARE</p>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 52px)', color: '#1c1917', marginBottom: '22px', lineHeight: '1.18' }}>
            We Did Not Build a Platform.<br />We Built a Promise.
          </h1>
          <p style={{ fontSize: '17px', color: '#6b7280', lineHeight: '1.85', maxWidth: '600px', margin: '0 auto' }}>
            Weddyfyp was started by people who believed that finding the right life partner should be a dignified, joyful experience — not a stressful scroll through thousands of strangers. That belief shapes everything we do.
          </p>
        </div>
      </section>

      {/* ── Our Story ── */}
      <section style={{ padding: '80px 24px', background: '#fff' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '64px', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: '12px', color: '#C2185B', fontWeight: '700', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '14px' }}>THE WEDDYFYP STORY</p>
            <h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', color: '#1c1917', marginBottom: '22px', lineHeight: '1.25' }}>
              Born From a Simple Observation
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280', lineHeight: '1.9', marginBottom: '18px' }}>
              We watched people around us — brothers, sisters, cousins, friends — struggle with online matrimony. Too many profiles, too little authenticity. Plenty of options, but very few genuine conversations. The experience felt more like browsing a catalogue than beginning a relationship.
            </p>
            <p style={{ fontSize: '16px', color: '#6b7280', lineHeight: '1.9', marginBottom: '18px' }}>
              Weddyfyp was built to be different. Smaller in volume, higher in quality. Every feature was designed with one question: does this help two people find each other more genuinely?
            </p>
            <p style={{ fontSize: '16px', color: '#6b7280', lineHeight: '1.9', marginBottom: '32px' }}>
              We serve members from all communities, states, and backgrounds — Hindu, Muslim, Christian, Sikh, and more. From Hyderabad to Bengaluru, from Chennai to Mumbai, and from India to the world.
            </p>
            <div style={{ padding: '18px 22px', background: '#fce4ec', borderRadius: '14px', borderLeft: '4px solid #C2185B' }}>
              <p style={{ fontSize: '14px', color: '#374151', fontStyle: 'italic', lineHeight: '1.7' }}>
                "This website is strictly for matrimonial purpose only and not a dating website."
              </p>
            </div>
          </div>

          {/* Visual blocks */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px' }}>
            {[
              { icon: '📱', label: 'Mobile Verified', sub: 'Every single profile' },
              { icon: '🔒', label: 'Privacy First', sub: 'You stay in control' },
              { icon: '💑', label: 'Genuine Matches', sub: 'Quality over quantity' },
              { icon: '🌍', label: 'All Communities', sub: 'Across India & world' },
            ].map((s, i) => (
              <div key={i} style={{
                textAlign: 'center', padding: '28px 16px', borderRadius: '18px',
                background: i % 2 === 0 ? '#fce4ec' : '#fdf5f8',
                border: `1px solid ${i % 2 === 0 ? '#f8bbd0' : '#f8bbd0'}`,
                transition: 'transform 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div style={{ fontSize: '30px', marginBottom: '10px' }}>{s.icon}</div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>{s.label}</div>
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pillars ── */}
      <section style={{ padding: '80px 24px', background: '#faf8f7' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <p style={{ fontSize: '12px', color: '#C2185B', fontWeight: '700', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '14px' }}>WHAT WE STAND FOR</p>
            <h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', color: '#1c1917' }}>The Four Pillars of Weddyfyp</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px' }}>
            {pillars.map((v, i) => (
              <div key={i} style={{
                background: '#fff', borderRadius: '20px', padding: '32px 26px',
                border: '1px solid #f3f4f6', textAlign: 'center',
                transition: 'box-shadow 0.25s, border-color 0.25s, transform 0.25s',
              }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 12px 36px rgba(194,24,91,0.08)'; e.currentTarget.style.borderColor = '#f8bbd0'; e.currentTarget.style.transform = 'translateY(-5px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#f3f4f6'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ fontSize: '38px', marginBottom: '16px' }}>{v.icon}</div>
                <h3 style={{ fontSize: '17px', color: '#1f2937', marginBottom: '10px' }}>{v.title}</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.78' }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section style={{ padding: '80px 24px', background: '#fff' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <p style={{ fontSize: '12px', color: '#C2185B', fontWeight: '700', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '14px' }}>WHY CHOOSE WEDDYFYP</p>
            <h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', color: '#1c1917' }}>We Take Your Search as Seriously as You Do</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px' }}>
            {whyPoints.map((w, i) => (
              <div key={i} style={{ display: 'flex', gap: '16px', padding: '24px', borderRadius: '16px', border: '1px solid #f3f4f6', background: '#faf8f7' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>{w.icon}</div>
                <div>
                  <h4 style={{ fontSize: '15px', color: '#1f2937', fontWeight: '700', marginBottom: '6px' }}>{w.heading}</h4>
                  <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.72' }}>{w.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Reach Out ── */}
      <section style={{
        padding: '72px 24px',
        background: 'linear-gradient(135deg, #C2185B, #AD1457)',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 4vw, 38px)', color: '#fff', marginBottom: '14px' }}>Have Questions? We'd Love to Talk.</h2>
          <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.82)', marginBottom: '36px', lineHeight: '1.7' }}>
            Whether you need help with your profile, have a concern, or simply want to know more about how Weddyfyp works — we are here.
          </p>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" style={{
              padding: '14px 36px', borderRadius: '50px', background: '#fff', color: '#C2185B',
              fontWeight: '700', fontSize: '15px', display: 'inline-block',
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)', transition: 'transform 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Contact Us →
            </Link>
            <a href="https://play.google.com/store" target="_blank" rel="noreferrer" style={{
              padding: '14px 36px', borderRadius: '50px',
              border: '2px solid rgba(255,255,255,0.55)', color: '#fff',
              fontWeight: '600', fontSize: '15px', display: 'inline-block',
              transition: 'border-color 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#fff'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.55)'}
            >
              Download the App
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
